import React, { useEffect, useState } from 'react';
import { forTemplate } from 'app/common/lib/util/i18n';
import cx from 'classnames';
import styles from './SelectCover.less';
import { SelectCoverEmpty } from './SelectCoverEmpty';
import { Spinner } from 'app/src/components/Spinner';
import {
  smallestPreviewBiggerThan,
  biggestPreview,
  Preview,
} from 'app/common/lib/util/preview.ts';
import {
  useSelectCoverQuery,
  useUpdateCardCoverMutation,
} from '@trello/graphql/generated';
import Icon, { IconName } from 'app/common/components/controls/icon';
import { track } from '@trello/analytics';

const format = forTemplate('card_cover_chooser');

enum CoverType {
  Attachment = 'attachment',
  Unsplash = 'unsplash',
}

export interface PotentialCover {
  id: string;
  type: CoverType;
  thumbnailUrl: string;
  edgeColor: string;
  selected: boolean;
}

/**
 * Given all of a card's attachments, getPotentialCovers will return
 * info about the attachments that are valid to set as Card Covers.
 *
 * @param attachments The attachments field on a Card model
 * @returns A list of potential covers for this card
 */
export const getPotentialCovers = (
  attachments: {
    id: string;
    edgeColor?: string | null;
    previews?: Preview[] | null;
  }[] = [],
  idAttachmentCover: string | null,
) => {
  const potentialCovers: PotentialCover[] = [];

  for (const attachment of attachments) {
    const preview =
      smallestPreviewBiggerThan(attachment.previews, 256) ||
      biggestPreview(attachment.previews);

    if (!preview || !preview.url) {
      continue;
    }

    potentialCovers.unshift({
      id: attachment.id,
      type: CoverType.Attachment,
      edgeColor: attachment.edgeColor || 'transparent',
      thumbnailUrl: preview.url,
      selected: idAttachmentCover === attachment.id,
    });
  }

  return potentialCovers;
};

interface SelectCoverProps {
  navigateToAddCover: () => void;
  cardId: string;
}

export const SelectCover: React.FunctionComponent<SelectCoverProps> = ({
  navigateToAddCover,
  cardId,
}) => {
  const { data, error, loading } = useSelectCoverQuery({
    variables: { cardId },
    fetchPolicy: 'cache-and-network', // TODO: Remove when ModelCache syncing is ready
  });
  const [updateCardCover] = useUpdateCardCoverMutation();
  const [
    lastUploadedBackgroundCover,
    setLastUploadedBackgroundCover,
  ] = useState();

  const setCover = async (
    cover: {
      idAttachment?: string;
      idUploadedBackground?: string;
    } | null,
  ) => {
    await updateCardCover({
      variables: {
        cardId,
        cover,
      },
    });
  };

  const card = data && data.card;
  const idAttachmentCover =
    card && card.idAttachmentCover ? card.idAttachmentCover : null;
  const cover = card ? card.cover : null;
  const potentialCovers =
    card && card.attachments
      ? getPotentialCovers(card.attachments, idAttachmentCover)
      : [];

  useEffect(
    () => {
      if (cover && cover.idUploadedBackground) {
        setLastUploadedBackgroundCover(cover);
      }
    },
    [cover],
  );

  const usingUnsplashCover = Boolean(
    cover && cover.idUploadedBackground && cover.scaled,
  );
  const unsplashCover = usingUnsplashCover
    ? cover
    : lastUploadedBackgroundCover;

  if (
    unsplashCover &&
    unsplashCover.idUploadedBackground &&
    unsplashCover.scaled
  ) {
    const preview =
      smallestPreviewBiggerThan(unsplashCover.scaled, 256) ||
      biggestPreview(unsplashCover.scaled);

    if (preview && preview.url) {
      potentialCovers.unshift({
        id: unsplashCover.idUploadedBackground,
        edgeColor: unsplashCover.edgeColor || 'transparent',
        thumbnailUrl: preview.url,
        selected: usingUnsplashCover,
        type: CoverType.Unsplash,
      });
    }
  }

  const isCoverSelected = potentialCovers.some(
    (potentialCover) => potentialCover.selected,
  );

  const isEmptyState = !loading && potentialCovers.length === 0;
  const isNonEmptyState = !loading && potentialCovers.length > 0;

  return (
    <div>
      {loading && <Spinner centered />}
      {error && format('something-went-wrong')}
      {isEmptyState && (
        <SelectCoverEmpty navigateToAddCover={navigateToAddCover} />
      )}
      {isNonEmptyState && (
        <>
          <div className={styles.coverTiles}>
            {potentialCovers.map(
              ({ id, type, edgeColor, thumbnailUrl, selected }) => (
                <button
                  key={id}
                  className={cx('button', styles.coverTilesItem, {
                    [styles.selected]: selected,
                  })}
                  style={{
                    backgroundColor: edgeColor,
                    backgroundImage: `url('${thumbnailUrl}')`,
                    backgroundSize:
                      type === CoverType.Unsplash ? 'cover' : 'contain',
                  }}
                  onClick={() => {
                    if (selected) {
                      setCover(null);
                      const previousCoverType =
                        type === CoverType.Unsplash
                          ? 'Unsplash'
                          : type === CoverType.Attachment
                            ? 'Attachment'
                            : 'Unknown';
                      track(
                        'Card Cover Chooser',
                        'Toggled Cover Off',
                        previousCoverType,
                      );
                    } else if (type === CoverType.Attachment) {
                      setCover({
                        idAttachment: id,
                      });
                      track('Card Cover Chooser', 'Chose Cover', 'Attachment');
                    } else if (type === CoverType.Unsplash) {
                      setCover({
                        idUploadedBackground: id,
                      });
                      track('Card Cover Chooser', 'Chose Cover', 'Unsplash');
                    }
                  }}
                />
              ),
            )}
            <button
              className={cx('button', styles.coverTilesItem, styles.modAdd)}
              onClick={navigateToAddCover}
              aria-label={format('upload-a-cover-image')}
            >
              <Icon small name={IconName.Add} />
            </button>
          </div>
          <button
            className={cx(
              'button',
              {
                disabled: !isCoverSelected,
              },
              styles.remove,
            )}
            onClick={() => {
              setCover(null);
              track('Card Cover Chooser', 'Remove Cover');
            }}
            disabled={!isCoverSelected}
          >
            {format('remove-cover')}
          </button>
        </>
      )}
    </div>
  );
};
